import {DeepPartial} from 'utility-types';

import {useEffect, useRef} from 'react';
import {UseFormWatch} from 'react-hook-form';

import {pick} from 'ramda';

import {VehicleSimplifiedTransactionFormRef} from '../VehicleSimplifiedTransactionForm';
import {VehicleTransactionFormRef} from '../VehicleTransactionForm';
import {
  VehicleTransactionFormSchemaType,
  BaseTransactionFormSchemaType,
} from '../VehicleTransactionFormSchema';

type RefType = VehicleTransactionFormRef | VehicleSimplifiedTransactionFormRef;

interface UseSubscribeToFormChangesProps<T extends RefType> {
  onChange: (
    value: VehicleTransactionFormSchemaType | BaseTransactionFormSchemaType,
    args: {
      formApi: T['formApi'] | undefined;
      formValues: DeepPartial<
        T extends VehicleTransactionFormRef
          ? VehicleTransactionFormSchemaType
          : BaseTransactionFormSchemaType
      >;
    }
  ) => void;
  watchFields: T extends VehicleTransactionFormRef
    ? (keyof VehicleTransactionFormSchemaType)[]
    : (keyof BaseTransactionFormSchemaType)[];
}

export function useSubscribeToFormChanges<T extends RefType>({
  onChange,
  watchFields,
}: UseSubscribeToFormChangesProps<T>) {
  const formRef = useRef<T>(null);

  useEffect(() => {
    const formApi = formRef.current?.formApi;
    const watch = formApi?.watch as UseFormWatch<
      VehicleTransactionFormSchemaType | BaseTransactionFormSchemaType
    >;

    if (!watch) {
      return undefined;
    }

    const subscription = watch((formValues, info) => {
      const name = info.name;

      if (name && (watchFields as string[]).includes(name)) {
        // @ts-expect-error - we know that formValues is a VehicleTransactionFormSchemaType or BaseTransactionFormSchemaType
        const changedFields = pick(watchFields, formValues);

        // @ts-expect-error - we know that formValues is a VehicleTransactionFormSchemaType or BaseTransactionFormSchemaType
        onChange(changedFields, {
          formApi,
          formValues,
        });
      }
    });

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [watchFields, onChange]);

  return formRef;
}
